// ---------------------------------------------------------------------------
// WhatsApp: quem quer (ou não) receber avisos e novidades pelo WhatsApp.
// A resposta fica em `leads` (aceita_whatsapp). Aqui só falamos com o banco
// (`meu_whatsapp` / `definir_meu_whatsapp`) e, quando a pessoa ainda não tinha
// telefone no perfil, gravamos o número pelo mesmo caminho de "Meus dados"
// (`salvar_meu_perfil`). Nada é criado no banco — as funções já existem.
// ---------------------------------------------------------------------------

import { supabase } from './supabase';

export interface MeuWhatsapp {
  logado: boolean;
  telefone: string | null;
  aceita: boolean | null;
  respondido_em: string | null;
  precisa_perguntar: boolean;
}

// Só dígitos do número, sem o 55 do Brasil e no máximo 11 (DDD + número).
export function digitosWhats(v?: string | null): string {
  let d = (v ?? '').replace(/\D/g, '');
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2);
  return d.slice(0, 11);
}

// (00) 00000-0000
export function formatarWhatsapp(v?: string | null): string {
  const d = digitosWhats(v);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

// Máscara enquanto a pessoa digita.
export function mascaraWhatsapp(v: string): string {
  return formatarWhatsapp(v);
}

export async function buscarMeuWhatsapp(): Promise<MeuWhatsapp> {
  const { data, error } = await supabase.rpc('meu_whatsapp');
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  return {
    logado: d.logado === true,
    telefone: typeof d.telefone === 'string' ? d.telefone : null,
    aceita: typeof d.aceita === 'boolean' ? d.aceita : null,
    respondido_em: typeof d.respondido_em === 'string' ? d.respondido_em : null,
    precisa_perguntar: d.precisa_perguntar === true,
  };
}

export interface ResultadoWhatsapp {
  ok: boolean;
  aceita: boolean | null;
  telefone: string | null;
}

export async function definirMeuWhatsapp(
  aceita: boolean,
  telefone?: string | null,
): Promise<ResultadoWhatsapp> {
  const limpo = telefone ? digitosWhats(telefone) : '';
  const { data, error } = await supabase.rpc('definir_meu_whatsapp', {
    p_aceita: aceita,
    p_telefone: limpo.length >= 10 ? limpo : null,
  });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  return {
    ok: d.ok === true,
    aceita: typeof d.aceita === 'boolean' ? d.aceita : null,
    telefone: typeof d.telefone === 'string' ? d.telefone : null,
  };
}

// Espelha o telefone no perfil (mesmo caminho de "Meus dados"). O banco ignora
// campos em branco, então mandar só o telefone não apaga os outros dados.
export async function salvarTelefoneNoPerfil(telefone: string): Promise<void> {
  const limpo = digitosWhats(telefone);
  if (limpo.length < 10) return;

  const {
    data: { session },
  } = await supabase.auth.getSession();
  const user = session?.user ?? null;

  let nome =
    (user?.user_metadata?.full_name as string | undefined) ||
    (user?.user_metadata?.name as string | undefined) ||
    '';

  try {
    const { data: perfil } = await supabase.from('vw_meu_perfil').select('nome').maybeSingle();
    const nomePerfil = (perfil as { nome?: string } | null)?.nome;
    if (typeof nomePerfil === 'string' && nomePerfil) nome = nomePerfil;
  } catch {
    /* segue com o nome da conta */
  }

  if (!nome) nome = user?.email?.split('@')[0] || 'Sem nome';

  const { error } = await supabase.rpc('salvar_meu_perfil', {
    p_nome: nome,
    p_telefone: limpo,
  });
  if (error) throw error;
}

// Rotas em que a folha NÃO aparece: pagamento/Pix, checkout, ficha de inscrição,
// login, caixa, portaria, barraca, fim do evento. O modo equipe é conferido à parte.
const PREFIXOS_BLOQUEADOS = [
  '/entrar',
  '/recuperar-senha',
  '/nova-senha',
  '/ingresso',
  '/pedido',
  '/loja/pedido',
  '/loja/barraca',
  '/inscricao',
  '/carteira',
  '/caixa',
  '/portaria',
  '/servir',
];

export function rotaBloqueiaWhatsapp(pathname: string): boolean {
  if (pathname === '/t' || pathname.startsWith('/t/')) return true; // talão / Pix
  return PREFIXOS_BLOQUEADOS.some((p) => pathname.startsWith(p));
}